import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Location } from '@angular/common'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { Event as RouterEvent, NavigationEnd, Router } from '@angular/router'
import { Subscription } from 'rxjs/Subscription'

import { AuthService } from '../services'
import { AnalyticsService, HOSTED_EVENT_NAMES } from '../analytics'
import { ReactFrameComponent } from './widgets/react-frame'
import { buildReactAppUrl, isReactAppPath, isSameOriginPath, stripQueryAndHash } from './utils/embed-url'
import { buildNavigateMessage, readShellMessage, ShellTrackMessage } from './utils/shell-bridge'

/**
 * Hosts a React screen inside the Angular shell.
 *
 * The React app is served from the same origin under /v2, so it shares localStorage (and
 * therefore the session) with this app — nothing is handed over. This component only:
 *  - derives the iframe URL from the Angular route, so refresh/bookmarks keep working;
 *  - mirrors the route reported by the iframe into the browser URL bar;
 *  - forwards navigations in both directions so moving between the two apps never reloads a
 *    whole document.
 *
 * The routes that use it are declared in ./routes.ts.
 */
@Component({
  selector: 'app-react-app',
  templateUrl: './react-app.component.html',
  styleUrls: ['./react-app.component.styl']
})
export class ReactAppComponent implements OnInit, OnDestroy {
  public frameUrl!: SafeResourceUrl

  @ViewChild(ReactFrameComponent) private frame!: ReactFrameComponent

  private hostedPath!: string
  private routeSubscription!: Subscription

  constructor (
    private router: Router,
    private location: Location,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private analytics: AnalyticsService
  ) {}

  public ngOnInit (): void {
    this.hostedPath = stripQueryAndHash(this.router.url)
    this.frameUrl = this.sanitize(this.router.url)

    this.routeSubscription = this.router.events
      .subscribe((event: RouterEvent) => {
        if (event instanceof NavigationEnd) {
          this.onNavigationEnd(event.urlAfterRedirects)
        }
      })
  }

  public ngOnDestroy (): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe()
    }
  }

  @HostListener('window:message', ['$event'])
  public onWindowMessage (event: MessageEvent): void {
    const message = readShellMessage(event, this.frame ? this.frame.contentWindow : null)
    if (!message) {
      return
    }

    switch (message.type) {
      case 'route-changed':
        this.mirrorUrl(message.path)
        break
      case 'navigate':
        this.navigateTo(message.path)
        break
      case 'session-expired':
        this.onSessionExpired()
        break
      case 'track':
        this.trackHostedEvent(message)
        break
    }
  }

  /**
   * Angular navigated somewhere else. If the React app is already mounted we ask it to route
   * instead of replacing the iframe src (that would reload the hosted document).
   */
  private onNavigationEnd (url: string): void {
    if (!isReactAppPath(url)) {
      return
    }

    const path = stripQueryAndHash(url)
    if (this.frame && !this.frame.isLoading) {
      this.hostedPath = path
      this.frame.postMessage(buildNavigateMessage(path))
      return
    }

    if (this.hostedPath === path) {
      return
    }

    this.hostedPath = path
    this.frameUrl = this.sanitize(url)
  }

  /** Keeps the address bar truthful without triggering an Angular navigation. */
  private mirrorUrl (path: string): void {
    const barePath = stripQueryAndHash(path)
    if (!isReactAppPath(barePath) || this.hostedPath === barePath) {
      return
    }

    this.hostedPath = barePath
    this.location.replaceState(path)
  }

  private navigateTo (path: string): void {
    if (!isSameOriginPath(path)) {
      return
    }
    this.router.navigateByUrl(path)
  }

  /**
   * The embedded app reported an expired session. It is only a hint: this app owns the
   * session, and it must not be destroyed on a false positive.
   */
  private onSessionExpired (): void {
    if (this.authService.isLoggedIn()) {
      console.warn('[react-app] Embedded app reported an expired session, but the shell still considers it valid.')
      return
    }
    this.authService.safeLogout()
  }

  private sanitize (url: string): SafeResourceUrl {
    // iframe src is a resource URL: Angular refuses unsafe values without this bypass.
    return this.sanitizer.bypassSecurityTrustResourceUrl(buildReactAppUrl(url))
  }

  /**
   * Analytics events raised by the hosted React app, which has no Amplitude client of its
   * own. The payload arrived already narrowed by `readShellMessage`; what is left to decide
   * here is whether this app reports that event at all.
   */
  private trackHostedEvent (message: ShellTrackMessage): void {
    if (HOSTED_EVENT_NAMES.indexOf(message.name) === -1) {
      console.warn(`[react-app] Ignoring analytics event "${message.name}": not in HOSTED_EVENT_NAMES.`)
      return
    }

    this.analytics.track({ name: message.name, properties: message.properties })
  }
}
